import { useLazyQuery, useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import AutocompleteWithFetch from 'components/Inputs/AutoCompletewithFetch';
import TextField from 'components/Inputs/TextField';
import {
  CREATE_USER_MUTATION,
  CreateUserResponse,
  CreateUserVariables,
} from 'graphql/mutation/user';

import {
  GET_BANDS_QUERY,
  GET_DEPARTMENTS_QUERY,
  GET_DESIGNATIONS_QUERY,
  GET_HODs_QUERY,
  GET_LEVELS_QUERY,
  GET_ROLES_QUERY,
  GET_USERS_FOR_AC,
  GetBandsQueryResponse,
  GetBandsQueryVariables,
  GetHODsResponse,
  GetLevelsQueryResponse,
  GetLevelsQueryVariables,
  GetRolesResponse,
  GetUserDepartmentResponse,
  GetUserDepartmentVariables,
  GetUserDesignationsResponse,
  GetUserDesignationsVariables,
  GetUsersQueryResponse,
  GetUsersQueryVariables,
} from 'graphql/query/user';
import { useState } from 'react';
import { toast } from 'react-toastify';

const CreateEmployeeForm = () => {
  const [formstate, setFormstate] = useState<Record<string, any>>({});

  const [error, setError] = useState('');

  const [getUserDepartment, { data: departments, loading: loadingDepartments }] = useLazyQuery<
    GetUserDepartmentResponse,
    GetUserDepartmentVariables
  >(GET_DEPARTMENTS_QUERY);

  const [getUsers, { data: users, loading: loadingUsers }] = useLazyQuery<
    GetUsersQueryResponse,
    GetUsersQueryVariables
  >(GET_USERS_FOR_AC);

  const [getBands, { data: bands, loading: loadingBands }] = useLazyQuery<
    GetBandsQueryResponse,
    GetBandsQueryVariables
  >(GET_BANDS_QUERY);

  const [getLevels, { data: levels, loading: loadingLevels }] = useLazyQuery<
    GetLevelsQueryResponse,
    GetLevelsQueryVariables
  >(GET_LEVELS_QUERY);

  const [getUserDesignations, { data: designations, loading: loadingDesignations }] = useLazyQuery<
    GetUserDesignationsResponse,
    GetUserDesignationsVariables
  >(GET_DESIGNATIONS_QUERY);

  const [getHODs, { data: HODs, loading: loadingHODs }] =
    useLazyQuery<GetHODsResponse>(GET_HODs_QUERY);

  const [getRoles, { data: roles, loading: loadingRoles }] =
    useLazyQuery<GetRolesResponse>(GET_ROLES_QUERY);

  const [createUser, { loading: creatingUser }] = useMutation<
    CreateUserResponse,
    CreateUserVariables
  >(CREATE_USER_MUTATION);

  const handleChange = (fieldName: string, value: any) => {
    setFormstate(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = () => {
    const refinedData = {
      ...formstate,
      role: formstate.role._id,
      department: formstate.department._id,
      designation: formstate.designation._id,
      band: formstate.band._id,
      level: levels?.getLevels[0]!,
      reportingManager: formstate.reportingManager._id,
      hod: formstate.hod._id,
    };

    createUser({
      variables: {
        //@ts-ignore
        input: refinedData,
      },
      onCompleted: () => {
        setFormstate({});
        toast.success('Employee Added Successfully');
      },
    });
  };

  const handleDesignationChange = (val: any) => {
    const obj = {
      designation: val,
      band: '',
      level: '',
    };
    setFormstate(prev => ({
      ...prev,
      ...obj,
    }));
  };

  const handleBandChange = (val: any) => {
    const obj = {
      band: val,
      level: '',
    };
    setFormstate(prev => ({
      ...prev,
      ...obj,
    }));

    if (!!val._id) {
      getLevels({
        variables: {
          band: val._id,
          designation: formstate.designation?._id,
        },
      });
    }
  };

  return (
    <Grid item xs={12}>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Grid container columnSpacing={2} rowGap={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Emp Id"
              name="empId"
              value={formstate.empId ?? ''}
              onChange={e => handleChange('empId', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              name="email"
              value={formstate.email ?? ''}
              onChange={e => handleChange('email', e.target.value)}
              type="email"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Name"
              name="name"
              value={formstate.name ?? ''}
              onChange={e => handleChange('name', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Location"
              value={formstate.location ?? ''}
              onChange={e => handleChange('location', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getRoles}
              label="Role"
              loading={loadingRoles}
              options={roles?.getRoles ?? []}
              value={formstate.role ?? { _id: '', name: '' }}
              handleChange={val => handleChange('role', val)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUserDepartment}
              label="Department"
              loading={loadingDepartments}
              value={formstate.department ?? { _id: '', name: '' }}
              handleChange={val => handleChange('department', val)}
              options={departments?.getUserDepartment ?? []}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUserDesignations}
              loading={loadingDesignations}
              options={designations?.getDesignations ?? []}
              label="Designation"
              value={formstate.designation ?? ''}
              handleChange={val => handleDesignationChange(val)}
              required
              disabled={!!!formstate.department}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getBands}
              loading={loadingBands}
              options={bands?.getBands ?? []}
              label="Band"
              value={formstate.band ?? ''}
              handleChange={val => handleBandChange(val)}
              required
              disabled={!!!formstate.designation}
              variables={{
                designation:
                  typeof formstate.designation === 'string'
                    ? formstate.designation
                    : formstate.designation?._id,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              loading={loadingLevels}
              label="Level"
              value={!!formstate.band?._id ? levels?.getLevels?.[0] : ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getHODs}
              label="HOD"
              loading={loadingHODs}
              value={formstate.hod ?? { _id: '', name: '' }}
              options={HODs?.getHODs ?? []}
              handleChange={val => handleChange('hod', val)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteWithFetch
              fetch={getUsers}
              label="Reporting Manager"
              handleChange={val => handleChange('reportingManager', val)}
              loading={loadingUsers}
              options={users?.getUsers ?? []}
              value={formstate.reportingManager ?? { _id: '', name: '' }}
              required
            />
          </Grid>

          <Grid item container justifyContent={'flex-end'} xs={12}>
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={!!error}
              loading={creatingUser}
            >
              ADD
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default CreateEmployeeForm;
